import {ChangeDetectionStrategy, Component, computed, inject, input, OnDestroy, OnInit, signal} from '@angular/core';
import {MaterialModule} from '../../../material.module';
import {
  ConnectNodesService,
  type ConnectWorkflowNodeCategory,
  type ConnectWorkflowNodePaletteItem
} from '../../../services/connect/connect-nodes.service';
import {ConnectProviderDef, ConnectProvidersService} from '../../../services/connect/connect-providers.service';
import {NgDiagramPaletteItem, NgDiagramPaletteItemComponent, NgDiagramPaletteItemPreviewComponent} from 'ng-diagram';
import {of, Subscription} from 'rxjs';
import {catchError, finalize, take} from 'rxjs/operators';
import {CONNECT_WORKFLOW_NODE_CATEGORIES, CONNECT_WORKFLOW_NODE_TYPES} from './connect-workflow-node-manifest';
import {isConnectWorkflowVisualFixtureUrl} from './connect-workflow-visual-fixture';

type ConnectDiagramNodeData = {
  label: string;
  ref?: string;
  type?: string;
  description?: string;
  providerId?: string;
  providerName?: string;
  providerLogo?: string;
  providerPalette?: ConnectProviderDef['palette'];
  fill?: string;
  stroke?: string;
  opacity?: number;
  radius?: number;
  locked?: boolean;
  [key: string]: unknown;
};

type PaletteViewItem = ConnectWorkflowNodePaletteItem & {diagramItem: NgDiagramPaletteItem<ConnectDiagramNodeData>};
type ProviderGroupView = {providerId: string; provider?: ConnectProviderDef; items: PaletteViewItem[]};

const CANVAS_SECTION_PALETTE_ID = 'connect.section';
const CANVAS_SECTION_NODE: ConnectWorkflowNodePaletteItem = {
  paletteId: CANVAS_SECTION_PALETTE_ID,
  workflowType: CANVAS_SECTION_PALETTE_ID,
  label: 'Section',
  description: 'Canvas section (resizable frame)',
  category: 'Utils',
  defaults: {label: 'Section'},
};

@Component({
  selector: 'app-connect-workflow-node-palette',
  standalone: true,
    imports: [MaterialModule, NgDiagramPaletteItemComponent, NgDiagramPaletteItemPreviewComponent],
  templateUrl: './connect-workflow-node-palette.component.html',
  styleUrls: ['./connect-workflow-node-palette.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConnectWorkflowNodePaletteComponent implements OnInit, OnDestroy {
  private readonly providersService = inject(ConnectProvidersService);
  private readonly autoExpandFixtureProviders =
    typeof window !== 'undefined' && !!window.location && isConnectWorkflowVisualFixtureUrl(window.location.href);
  readonly catalogNodeTypes = signal<ConnectWorkflowNodePaletteItem[] | null>(null);
  private sub = new Subscription();

  nodeTypes = input<ConnectWorkflowNodePaletteItem[]>([]);
  hasAnyTriggerNodes = input<boolean>(true);

  readonly categories = CONNECT_WORKFLOW_NODE_CATEGORIES;
  readonly query = signal<string>('');
  readonly collapsed = signal<boolean>(false);
  readonly expanded = signal<Record<ConnectWorkflowNodeCategory, boolean>>({
    Triggers: false,
    Logic: false,
    Integrations: false,
    Utils: false
  });
  readonly showAll = signal<Record<ConnectWorkflowNodeCategory, boolean>>({
    Triggers: false,
    Logic: false,
    Integrations: false,
    Utils: false
  });
  readonly providerExpanded = signal<Record<string, boolean>>({});
    readonly catalogLoading = signal<boolean>(false);
    private readonly nodesService = inject(ConnectNodesService);

  readonly providers = signal<ConnectProviderDef[]>([]);
  readonly providersLoading = signal<boolean>(false);

  readonly providerMeta = computed(() => {
    const map = new Map<string, ConnectProviderDef>();
    for (const p of this.providers()) {
      map.set(p.id, p);
    }
    return map;
  });
  readonly itemsByCategory = computed(() => {
    const grouped: Record<ConnectWorkflowNodeCategory, PaletteViewItem[]> = {
      Triggers: [],
      Logic: [],
      Integrations: [],
      Utils: []
    };
    for (const n of this.viewItems()) {
      const cat = this.categories.includes(n.category) ? n.category : 'Logic';
      grouped[cat].push(n);
    }
    return grouped;
  });

  readonly viewItems = computed((): PaletteViewItem[] => {
    const q = this.query().trim().toLowerCase();
    const providerMeta = this.providerMeta();
    const items = q
      ? this.effectiveNodeTypes().filter(n => `${n.paletteId} ${n.label}`.toLowerCase().includes(q))
      : this.effectiveNodeTypes();

    return items.map(item => {
      const provider = item.providerId ? providerMeta.get(item.providerId) : undefined;
      return {
        ...item,
        diagramItem: this.toDiagramItem(item, provider),
      };
    });
  });
  private readonly effectiveNodeTypes = computed(() => {
      const catalog = this.catalogNodeTypes();
      const base = Array.isArray(catalog) && catalog.length > 0 ? catalog : CONNECT_WORKFLOW_NODE_TYPES;
    const merged: ConnectWorkflowNodePaletteItem[] = [];
    const seen = new Set<string>();
    for (const item of [CANVAS_SECTION_NODE, ...this.nodeTypes(), ...base]) {
      if (seen.has(item.paletteId)) continue;
      seen.add(item.paletteId);
      merged.push(item);
    }
    return merged;
  });

  readonly providerGroups = computed((): ProviderGroupView[] => {
    const providerMeta = this.providerMeta();
    const groups = new Map<string, PaletteViewItem[]>();
    for (const item of this.itemsByCategory().Integrations) {
      if (!item.providerId) continue;
      const list = groups.get(item.providerId) ?? [];
      list.push(item);
      groups.set(item.providerId, list);
    }

    return [...groups.entries()]
      .map(([providerId, items]) => ({providerId, provider: providerMeta.get(providerId), items}))
      .sort((a, b) => (a.provider?.name ?? a.providerId).localeCompare(b.provider?.name ?? b.providerId));
  });

  readonly totalMatches = computed(() => this.viewItems().length);

  ngOnInit(): void {
    this.expanded.set({
      Triggers: !this.hasAnyTriggerNodes(),
      Logic: false,
      Integrations: this.autoExpandFixtureProviders,
      Utils: false,
    });
    this.loadProviders();
    this.loadNodesCatalog();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  setQuery(value: string): void {
    this.query.set(value);
  }

  toggleCollapsed(): void {
    this.collapsed.set(!this.collapsed());
  }

  toggleCategory(category: ConnectWorkflowNodeCategory): void {
    this.expanded.update(prev => ({...prev, [category]: !prev[category]}));
  }

  toggleProvider(providerId: string): void {
    this.providerExpanded.update(prev => ({...prev, [providerId]: !prev[providerId]}));
  }

  setShowAll(category: ConnectWorkflowNodeCategory, value: boolean): void {
    this.showAll.update(prev => ({...prev, [category]: value}));
  }

  isCategoryExpanded(category: ConnectWorkflowNodeCategory): boolean {
    return this.query().trim().length > 0 || this.expanded()[category] === true;
  }

  isProviderExpanded(providerId: string): boolean {
    return this.query().trim().length > 0 || this.providerExpanded()[providerId] === true;
  }

  itemsForCategory(category: ConnectWorkflowNodeCategory): PaletteViewItem[] {
    return this.itemsByCategory()[category] ?? [];
  }

  ungroupedItemsForCategory(category: ConnectWorkflowNodeCategory): PaletteViewItem[] {
    const items = this.itemsForCategory(category);
    return category === 'Integrations' ? items.filter(i => !i.providerId) : items;
  }

  visibleUngroupedItemsForCategory(category: ConnectWorkflowNodeCategory): PaletteViewItem[] {
    const pageLimit = 20;
    const list = this.ungroupedItemsForCategory(category);
    if (this.query().trim().length > 0) return list;
    if (this.showAll()[category]) return list;
    if (list.length <= pageLimit) return list;
    return list.slice(0, pageLimit);
  }

  private loadProviders(): void {
    this.providersLoading.set(true);
    this.sub.add(
      this.providersService
        .fetchCatalog()
        .pipe(
          take(1),
          catchError(() => of(null)),
          finalize(() => this.providersLoading.set(false)),
        )
        .subscribe(catalog => {
          this.providers.set(Array.isArray(catalog?.providers) ? catalog!.providers : []);
          if (this.autoExpandFixtureProviders) {
            this.providerExpanded.set(
              this.providers().reduce<Record<string, boolean>>((acc, provider) => {
                acc[provider.id] = true;
                return acc;
              }, {}),
            );
          }
        }),
    );
  }

  private loadNodesCatalog(): void {
    this.catalogLoading.set(true);
    this.sub.add(
      this.nodesService
        .fetchCatalog()
        .pipe(
          take(1),
          catchError(() => of(null)),
          finalize(() => this.catalogLoading.set(false)),
        )
        .subscribe(catalog => {
          this.catalogNodeTypes.set(Array.isArray(catalog?.nodes) ? catalog!.nodes : []);
        }),
    );
  }

  private toDiagramItem(item: ConnectWorkflowNodePaletteItem, provider?: ConnectProviderDef): NgDiagramPaletteItem<ConnectDiagramNodeData> {
    if (item.paletteId === CANVAS_SECTION_PALETTE_ID) {
      return {
        type: 'connect.background',
        autoSize: false,
        resizable: true,
        zOrder: -1000,
        size: {width: 560, height: 240},
        data: {
          label: item.label || 'Section',
          fill: '#e2e8f0',
          stroke: 'rgba(15, 23, 42, 0.14)',
          opacity: 0.25,
          radius: 18,
          locked: false,
        },
      };
    }

    const defaults = (item.defaults ?? {}) as Record<string, unknown>;
    const label = typeof defaults['label'] === 'string' && String(defaults['label']).trim() ? (defaults['label'] as string) : item.label;
    const ref = typeof defaults['ref'] === 'string' && String(defaults['ref']).trim() ? (defaults['ref'] as string) : item.workflowType;

    return {
      type: 'connect.default',
      data: {
        ...defaults,
        label,
        ref,
        type: item.workflowType,
        description: item.description,
        providerId: provider?.id ?? item.providerId,
        providerName: provider?.name,
        providerLogo: provider?.logo,
        providerPalette: provider?.palette,
      },
    };
  }
}
